// Import the RTK Query methods from the React-specific entry point
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { restApiUrl } from 'appConstants';
import { getCSRFCookie } from 'axios_middleware';

import { DjangoPaginatedResponse } from './types';

// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
// https://redux-toolkit.js.org/rtk-query/usage/prefetching

export type SavedRouteAlertMetricType =
  | 'PERCENT_SLOWDOWN'
  | 'TRAVEL_TIME_TYPICAL'
  | 'SPEED_TYPICAL'
  | 'TRAVEL_TIME'
  | 'SPEED';

export type SavedRouteAlertRoute = {
  id: string;
  name: string;
};

export type SavedRouteAlert = {
  hash_id: string;
  creator: string | null;
  created_on: string;
  updated_by: string | null;
  updated_on: string | null;
  project: string;
  is_enabled: boolean;
  has_resolution_email: boolean;
  schedule_day_types: number[];
  schedule_day_hours: number[];
  metric_type: SavedRouteAlertMetricType;
  typical_year_num: number | null;
  typical_month_num: number | null;
  metric_criteria: number;
  routes: SavedRouteAlertRoute[];
  recipients: string[]; // Array of email addresses
};

export type SavedRouteAlertList = SavedRouteAlert[];

export type MetricValue = {
  delay_seconds: number;
  percent_slowdown: number;
  average_speed_kmph: number;
};

export type SavedRouteAlertEvent = {
  hash_id: string;
  alert: string; // hash_id
  route: string; // hash_id
  route_name: string;
  ended_on: string | null;
  created_on: string;
  metric_type: SavedRouteAlertMetricType;
  metric_value: MetricValue;
  resolved: boolean;
  alert_metric_criteria: number;
  comparison_month: string;
};

export type SavedRouteAlertEventList = SavedRouteAlertEvent[];
export type SavedRouteAlertCreateInput = {
  is_enabled?: boolean;
  has_resolution_email?: boolean;
  schedule_day_types: number[];
  schedule_day_hours: number[];
  metric_type: SavedRouteAlertMetricType;
  typical_year_num?: number | null;
  typical_month_num?: number | null;
  metric_criteria: number;
  routes: SavedRouteAlertRoute[];
  recipients: string[];
};

export type SavedRouteAlertUpdateInput = Partial<SavedRouteAlertCreateInput>;

export const emailAlertsApiSlice = createApi({
  reducerPath: 'emailAlerts_api',
  baseQuery: fetchBaseQuery({
    baseUrl: restApiUrl,
    prepareHeaders: (headers) => {
      headers.append('X-CSRFToken', getCSRFCookie());
    },
  }),
  tagTypes: ['savedRouteAlerts', 'savedRouteAlertEvents', 'savedRoutes'],
  endpoints: (builder) => ({
    getSavedRouteAlertEvents: builder.query<
      DjangoPaginatedResponse<SavedRouteAlertEvent>,
      {
        project_slug: string;
        page: number;
        page_size: number;
        searchTerm?: string;
        date?: string;
        resolved?: number;
        routeId?: string;
      }
    >({
      query: ({
        project_slug,
        page,
        page_size,
        searchTerm,
        date,
        resolved,
        routeId,
      }) => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('page_size', String(page_size));
        if (searchTerm) params.set('search', searchTerm);
        if (date) params.set('date', date);
        if ([0, 1].includes(resolved)) params.set('resolved', String(resolved));
        if (routeId) params.set('route', routeId);

        return `saved_route_alert_events/${project_slug}/?${params.toString()}`;
      },
      providesTags: (result, error, { project_slug }) => [
        { type: 'savedRouteAlertEvents', id: project_slug },
      ],
    }),
    getSavedRouteAlerts: builder.query<SavedRouteAlertList, string>({
      query: (project_slug) => {
        return `saved_route_alerts/${project_slug}/`;
      },
      providesTags: (result, error, project_slug) => [
        { type: 'savedRouteAlerts', id: project_slug },
      ],
    }),

    getSavedRouteAlert: builder.query<
      SavedRouteAlert,
      { project_slug: string; alert_id: string }
    >({
      query: ({ project_slug, alert_id }) => {
        return `saved_route_alerts/${project_slug}/${alert_id}/`;
      },
      providesTags: (result, error, { project_slug, alert_id }) => [
        { type: 'savedRouteAlerts', id: project_slug },
        { type: 'savedRouteAlerts', id: `${project_slug}-${alert_id}` },
      ],
    }),

    createSavedRouteAlert: builder.mutation<
      SavedRouteAlert,
      { project_slug: string; data: SavedRouteAlertCreateInput }
    >({
      query: ({ project_slug, data }) => ({
        url: `saved_route_alerts/${project_slug}/`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { project_slug }) => [
        { type: 'savedRouteAlerts', id: project_slug },
        { type: 'savedRoutes' },
      ],
    }),

    updateSavedRouteAlert: builder.mutation<
      SavedRouteAlert,
      {
        project_slug: string;
        alert_id: string;
        data: SavedRouteAlertUpdateInput;
      }
    >({
      query: ({ project_slug, alert_id, data }) => {
        // exclude read-only fields
        const { creator, ...updateData } = data as any;
        return {
          url: `saved_route_alerts/${project_slug}/${alert_id}/`,
          method: 'PATCH',
          body: updateData,
        };
      },
      invalidatesTags: (result, error, { project_slug, alert_id }) => [
        { type: 'savedRouteAlerts', id: project_slug },
        { type: 'savedRouteAlerts', id: `${project_slug}-${alert_id}` },
        { type: 'savedRoutes' },
      ],
    }),

    deleteSavedRouteAlert: builder.mutation<
      void,
      { project_slug: string; alert_id: string }
    >({
      query: ({ project_slug, alert_id }) => ({
        url: `saved_route_alerts/${project_slug}/${alert_id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { project_slug }) => [
        { type: 'savedRouteAlerts', id: project_slug },
        { type: 'savedRoutes' },
      ],
    }),
  }),
});

export const {
  useGetSavedRouteAlertsQuery,
  useGetSavedRouteAlertQuery,
  useCreateSavedRouteAlertMutation,
  useUpdateSavedRouteAlertMutation,
  useDeleteSavedRouteAlertMutation,
  useGetSavedRouteAlertEventsQuery,
} = emailAlertsApiSlice;
